/**
 * Sign in.
 *
 * The server answers a bad email and a bad password identically and takes the
 * same time over both, so this screen must not undo that by being more helpful
 * — one message, whatever went wrong.
 */

import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { useSession } from "../session";
import { Icon } from "../components/Sprites";

/** Failure codes the OAuth callback redirects back with. */
const OAUTH_ERRORS: Record<string, string> = {
  oauth_declined: "You declined the sign-in request.",
  oauth_expired: "That sign-in attempt expired. Try again.",
  oauth_unavailable: "That sign-in provider is no longer configured.",
  oauth_failed: "The sign-in provider could not be reached. Try again.",
  oauth_unverified_email:
    "An account already exists with this email address, and the provider will not confirm the address belongs to you. Sign in with your password, then link the provider from settings.",
};

const PROVIDER_LABELS: Record<string, string> = {
  google: "Continue with Google",
  github: "Continue with GitHub",
};

export function Login() {
  const { reload } = useSession();
  const [params] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: providers } = useAsync<{ providers: string[] }>(
    (signal) => api.get<{ providers: string[] }>("/auth/providers", { signal, unscoped: true }),
    [],
  );

  const oauthError = params.get("error");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await api.post("/auth/login", { email, password }, { unscoped: true });
      await reload();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not reach the server. Check that it is running.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="login">
      <div className="oc-panel oc-starfield login__card">
        <div>
          {/* The mark, not the horizontal lockup. The lockup carries its own
              cream background rect, which would paint a warm panel on the white
              card; the mark is transparent and sits on whatever is behind it.
              The wordmark is redundant here anyway — the <h1> says it, and in
              text rather than as live Arial <text> inside an SVG. */}
          <svg
            width="40"
            height="40"
            viewBox="0 0 260 260"
            aria-hidden="true"
            focusable="false"
            style={{ display: "block", marginBottom: "var(--oc-space-3)" }}
          >
            <use href="#oc-logo-mark" />
          </svg>
          <h1 className="oc-headline-lg">OpenComms</h1>
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            Sign in to your dashboard.
          </p>
        </div>

        {oauthError && (
          <div className="oc-panel oc-panel--attention">
            <div className="oc-panel__body oc-body-sm">
              {OAUTH_ERRORS[oauthError] ?? "Sign-in did not complete. Try again."}
            </div>
          </div>
        )}

        <form className="stack" onSubmit={(event) => void submit(event)}>
          <div className="login__field">
            <label className="oc-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="oc-input"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="login__field">
            <label className="oc-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="oc-input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {error && (
            <div className="field-error" role="alert">
              {error}
            </div>
          )}

          <button type="submit" className="oc-btn oc-btn--primary" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {providers && providers.providers.length > 0 && (
          <>
            <div className="login__divider">or</div>
            <div className="stack">
              {providers.providers.map((name) => (
                <a
                  key={name}
                  className="oc-btn"
                  // A full navigation, not fetch: the provider redirect has to
                  // happen in the address bar for the user to see whose page
                  // they are typing a password into.
                  href={`/auth/oauth/${name}/start?return_to=${encodeURIComponent("/messages")}`}
                >
                  <Icon name="external" size={14} />
                  {PROVIDER_LABELS[name] ?? `Continue with ${name}`}
                </a>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
